import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect } from 'react';
import {
  AnalyticsTrigger,
  Button,
  Card,
  CardContent,
  Container,
  EqualHeight,
  EqualHeightProvider,
  Fittext,
  Grid,
  GridColumn,
  Heading,
  Icon,
  Modal,
  Notice,
  Separator,
  Spinner,
  Text,
} from 'telia-front-react';
import customerIdAsKey from '../product/withCustomerIdAsKey';
import SsoStore from '../stores/SsoStore';
import TvChannelsStore, { BusinessTvPacket } from '../stores/TvChannelsStore';
import TvB2BPacketDetailsContainer from '../tv/TvB2BPacketDetailsContainer';
import { renderMonthlyPrice } from '../util/PriceUtil';
import TvChannelList from './TvChannelList';
import { TranslationsContext } from '@teliaee/telia-translate';

export interface BusinessTvPacketsContainerProps {
  readMoreUrls: string[];
  readMoreText: string;
  title?: string;
  headingLevel?: number;
  iconSize?: string;
  size?: 'small' | 'medium' | 'large';
  align?: 'center' | 'right';
  color?: 'inverse';
  className?: string;
  tvChannelsStore?: TvChannelsStore;
  ssoStore?: SsoStore;
}

const BusinessTvPacketsContainer = (props: BusinessTvPacketsContainerProps) => {
  const translate = useContext(TranslationsContext).translate;
  const { tvChannelsStore, title, headingLevel = 2 } = props;
  useEffect(() => {
    tvChannelsStore.loadBusinessTvPacket();
  }, []);

  const renderPacketCard = (packet: BusinessTvPacket, index: number): React.ReactNode => {
    const { ssoStore, readMoreUrls, readMoreText, iconSize, size, align, color, className } = props;

    const header = translate(`businessTv.packets.title.${packet.productOfferingCode}`);

    if ((packet.liableOnlyForSalesConsultant && !ssoStore.isDealer) || !readMoreUrls[index]) {
      return;
    }

    return (
      <GridColumn key={index} width={['xs-12', 'sm-6', 'md-4', 'lg-3']}>
        <Card className="text-left" type={packet.liableOnlyForSalesConsultant ? 'employee' : undefined}>
          <CardContent>
            <Text>
              <Icon modifiers={translate(`businessTv.packets.icon.${packet.productOfferingCode}`)} size="xxxl" />
              <EqualHeight group="packet-title">
                <Heading level={headingLevel + 1} className="h3">
                  <Fittext>{header}</Fittext>
                </Heading>
              </EqualHeight>
              <strong className="h5">{`${renderMonthlyPrice(true, packet.price, translate)} ${translate('price.vat')}`}</strong>
              <p>
                <AnalyticsTrigger trigger={{ id: `CTA - ${header}`, callback: 'onClick' }}>
                  <Button
                    type={packet.liableOnlyForSalesConsultant ? 'employee' : 'expressive'}
                    text={readMoreText}
                    iconRight="#arrow-right"
                    url={readMoreUrls[index]}
                  />
                </AnalyticsTrigger>
              </p>
            </Text>
            <Separator spacing="xsmall" weight="thin" />
            <Text>
              <EqualHeight group="packet-description">
                <Text>
                  <p className="text-small text-light">{translate('businessTv.packets.description.prefix')}</p>
                  <p className="text-small">{translate(`businessTv.packets.description.${packet.productOfferingCode}`)}</p>
                </Text>
              </EqualHeight>
              <EqualHeight group="packet-channels">
                <TvChannelList
                  iconSize={iconSize}
                  size={size}
                  items={packet.channels}
                  color={color || 'inverse'}
                  align={align || 'center'}
                  className={className}
                />
              </EqualHeight>
              <Separator spacing="xsmall" weight="thin" />
              <Grid noWrap={true} align={['center-xs', 'middle-xs']} gutter="small" className="text-left">
                <GridColumn width={['min']}>
                  <Icon modifiers="#cherry" size="sm" align="none" />
                </GridColumn>
                <GridColumn width={['max']} className="text-small">
                  <p>{translate('businessTv.packets.additionalChannels.title')}</p>
                  <Button
                    text={translate('businessTv.packets.additionalChannels.button')}
                    link={true}
                    iconRight="#arrow-right"
                    onClick={(event) => {
                      event.preventDefault();
                      tvChannelsStore.chosenPacket = packet?.productOfferingCode;
                      tvChannelsStore.isTvB2BPacketDetailsModalOpen = true;
                    }}
                  />
                </GridColumn>
              </Grid>
            </Text>
          </CardContent>
        </Card>
      </GridColumn>
    );
  };

  if (['FETCHING', 'UNINITIALIZED'].includes(tvChannelsStore.businessTvPackets?.status)) {
    return <Spinner />;
  }

  if (tvChannelsStore.businessTvPackets.status === 'ERROR' || !tvChannelsStore.businessTvPackets.tvPackets?.length) {
    return (
      <Container>
        <Notice type={'error'}>{translate('general.error.message')}</Notice>
      </Container>
    );
  }

  return (
    <Container>
      <Text>
        {title && (
          <>
            <Heading level={headingLevel} className="h2 text-center">
              {title}
            </Heading>
            <Separator spacing="xsmall" color="invisible" />
          </>
        )}
        <EqualHeightProvider groups={['packet-title', 'packet-channels', 'packet-description']}>
          <Grid equalHeight={true} align={['center-xs']}>
            {tvChannelsStore.businessTvPackets.tvPackets?.map((packet: BusinessTvPacket, index: number) => renderPacketCard(packet, index))}
          </Grid>
        </EqualHeightProvider>
        <Modal size="8-col" isOpen={tvChannelsStore.isTvB2BPacketDetailsModalOpen} onClose={() => (tvChannelsStore.isTvB2BPacketDetailsModalOpen = false)}>
          <Text className="text-center">
            <p className="h2">{translate('businessTv.packets.modal.title')}</p>
          </Text>
          <Separator spacing="small" color="invisible" />
          <TvB2BPacketDetailsContainer chosenPacket={tvChannelsStore?.chosenPacket} isModal={false} />
        </Modal>
      </Text>
    </Container>
  );
};

export default inject('tvChannelsStore', 'ssoStore')(customerIdAsKey(observer(BusinessTvPacketsContainer)));
